import { RedoButton } from "@redotech/arbiter/buttons/redo-button";
import { Flex } from "@redotech/arbiter/flex";
import { Text } from "@redotech/arbiter/text";
import { AddressInfo } from "@redotech/redo-customer-portal-shared/hooks/useAddress/use-address";
import {
  useAddressDiff,
  useVerifyAddress,
  type VerifyAddress,
} from "@redotech/redo-customer-portal-shared/hooks/useVerifyAddress/use-verify-address";
import { ReturnAddressType } from "@redotech/redo-customer-portal-shared/navigator";
import { checkBlockingAddressErrors } from "@redotech/redo-customer-portal-shared/utils/address-validation";
import { useTranslation } from "@redotech/redo-i18n";
import { Address as AddressType } from "@redotech/redo-return-operations-common/draft-return/draft-return";
import {
  AddressSelection,
  toAddressFields,
} from "@redotech/redo-web/address-verification/address-suggestion";
import { filterTruthy } from "@redotech/util/array";
import { memo, useEffect, useMemo, useState } from "react";
import { AddressDisplay, AddressValidationError } from "./address-display";

export interface AddressValidationStatus {
  type: ReturnAddressType;
  isInvalid: boolean;
}

export function Address({
  addressInfo,
  onAddressSelected,
  onValidationStatusChanged,
  continueDisabled = false,
  skipComparison = false,
  verifyAddress,
}: {
  addressInfo: AddressInfo;
  onAddressSelected?: (type: AddressInfo["type"], address: AddressType) => void;
  onValidationStatusChanged?: (status: AddressValidationStatus) => void;
  continueDisabled?: boolean;
  skipComparison?: boolean;
  verifyAddress?: VerifyAddress;
}) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<AddressSelection>("suggested");
  const verifyAddressQuery = useVerifyAddress({
    address: addressInfo.address ?? undefined,
    verifyAddress,
  });

  const blockingError = useMemo(() => {
    return checkBlockingAddressErrors(
      addressInfo.address?.street1 ?? undefined,
      addressInfo.address?.street2 ?? undefined,
      addressInfo.address?.country ?? undefined,
      addressInfo.type,
    );
  }, [
    addressInfo.address?.street1,
    addressInfo.address?.street2,
    addressInfo.address?.country,
    addressInfo.type,
  ]);

  const hasVerificationError =
    (verifyAddressQuery.isSuccess &&
      !verifyAddressQuery.data?.verifications?.delivery?.success) ||
    verifyAddressQuery.isError;

  const isLoading = verifyAddressQuery.isPending;
  const suggestedAddress = verifyAddressQuery.data?.verifiedAddress;
  const { changedFields, changed: addressChanged } = useAddressDiff(
    addressInfo.address ?? undefined,
    suggestedAddress,
  );
  const isMissingEmail = !!addressInfo.address && !addressInfo.address.email;

  const showComparison =
    !skipComparison &&
    !blockingError &&
    !!addressInfo.address &&
    (isLoading || hasVerificationError || addressChanged);

  useEffect(() => {
    if (onAddressSelected && addressInfo.address) {
      if (selected === "suggested" && suggestedAddress) {
        onAddressSelected(addressInfo.type, {
          ...suggestedAddress,
          name: addressInfo.address.name,
          email: addressInfo.address.email,
          phone: addressInfo.address.phone,
        });
      } else {
        onAddressSelected(addressInfo.type, addressInfo.address);
      }
    }
    onValidationStatusChanged?.({
      type: addressInfo.type,
      isInvalid: hasVerificationError,
    });
  }, [
    selected,
    suggestedAddress,
    addressInfo.address,
    addressInfo.type,
    onAddressSelected,
    onValidationStatusChanged,
    hasVerificationError,
  ]);

  const missingFieldErrorMessage = continueDisabled
    ? addressInfo.requiredFieldErrorMessage ||
      (isMissingEmail
        ? t("Email is required.")
        : t("Please add required fields to address to continue."))
    : undefined;

  return (
    <AddressDisplay
      addressFields={
        addressInfo.address
          ? {
              ...toAddressFields(addressInfo.address),
              name: addressInfo.address.name,
            }
          : undefined
      }
      blockingErrorMessage={blockingError?.message}
      changedFields={changedFields}
      comparisonDisabled={isLoading || hasVerificationError}
      comparisonHasError={hasVerificationError}
      comparisonIsLoading={isLoading}
      continueDisabled={continueDisabled}
      missingEmailErrorMessage={missingFieldErrorMessage}
      onEdit={addressInfo.edit}
      onSelect={setSelected}
      selected={selected}
      showComparison={showComparison}
      suggestedAddressFields={
        suggestedAddress ? toAddressFields(suggestedAddress) : undefined
      }
      title={addressInfo.title}
    />
  );
}

const ShippingLineOfText = memo(function ShippingLineOfText({
  lineOfText,
}: {
  lineOfText: string;
}) {
  return (
    <Text fontSize="md" fontWeight="medium">
      {lineOfText}
    </Text>
  );
});

export const AddressOld = memo(function Address({
  address,
  title,
  edit,
  errorMessage,
  showBorder = true,
}: {
  address?: AddressType;
  title: string;
  edit?: () => void;
  errorMessage?: string;
  showBorder?: boolean;
}) {
  const { t } = useTranslation();
  let secondLine = "";
  if (address) {
    secondLine = filterTruthy([address.city, address.state]).join(", ");
    if (address.zip) {
      secondLine += ` ${address.zip}`;
    }
  }
  return (
    <Flex
      basis={0}
      borderColor={showBorder ? "secondary" : "transparent"}
      borderRadius="lg"
      borderStyle={showBorder ? "solid" : "none"}
      borderWidth="1px"
      data-ui="address"
      dir="column"
      gap="xs"
      grow={1}
      p={showBorder ? "3xl" : "none"}
    >
      <Text fontSize="sm" textColor="secondary">
        {title}
      </Text>
      {address && (
        <Flex dir="column" gap="none">
          <ShippingLineOfText lineOfText={address.name} />
          <ShippingLineOfText lineOfText={address.street1} />
          <ShippingLineOfText lineOfText={address.street2 || ""} />
          <ShippingLineOfText lineOfText={secondLine} />
          <ShippingLineOfText lineOfText={address.country} />
        </Flex>
      )}
      {!address && (
        <ShippingLineOfText lineOfText={t("Same as returning items from.")} />
      )}
      {errorMessage && <AddressValidationError errorMessage={errorMessage} />}
      {edit && (
        <RedoButton
          hierarchy="tertiary"
          onClick={() => edit()}
          text={t("Edit")}
        />
      )}
    </Flex>
  );
});
